import { expose } from 'comlink';
import { Logger } from '../../logger/logger';
import { LoggerConfig } from '../../logger/logger-config';
import { LogLevel } from '../../logger/enum/log-level';
import { ConsoleLogAppender } from '../../logger/appenders/console-log-appender';
import { HttpPollingRequestParam } from '@nice-ccf/core-sdk';
import { RetryOptions } from '../model/retry-options/retry-options';

const consoleAppender = new ConsoleLogAppender();
const loggerConfig = new LoggerConfig(LogLevel.INFO);
loggerConfig.addAppender(consoleAppender);

Logger.setConfig(loggerConfig);
const DIRECTORY_POLLING_INTERVAL = 5000;

/**
 * This will be a common worker which will handle the common worker operations such as handling timeouts, interval etc..
 */
export class UtilWorker {
  private logger: Logger = new Logger('Worker', 'util-worker.ts');

  /**
   *  Use to invoke the timeout after the time has passed and will execute the callback passed as param
   * @param timeoutInterval - time(in milliseconds) after which the timeout will be executed
   * @param callback - callback method which will be executed when time interval is completed
   * @example -
   * ```
   * startTimeout(timeoutInterval, proxy(this.callback.bind(this)));
   * ```
   */
  startTimeout = (timeoutInterval: number, callback: () => void) => {
    this.logger.info('startTimeout', `startTimeout triggered with ${timeoutInterval}ms interval`);
    setTimeout(() => {
      callback();
      this.logger.info('startTimeout', `setTimeout callback executed`);
    }, timeoutInterval);
  };

  /**
   * Perform api polling on web worker thread
   * @param url - api endpoint to be called
   * @param request - http request along with headers
   * @param retryOptions - retry interval (in milliseconds) and maxRetryAttempts
   * @param callback - method to invoke on completion
   * @example
   * ```
   * startPolling(url, request, retryOptions, proxy(callback.bind(this)))
   * ```
   */
  startPolling = async (
    requestParam: HttpPollingRequestParam | Array<HttpPollingRequestParam>,
    retryOptions: RetryOptions,
    callback: (data: any) => void,
    isPolling: boolean
  ) => {
    if (retryOptions) {
      this.logger.info(
        'startPolling',
        `startPolling triggered with ${retryOptions.retryInterval}ms interval`
      );
      if (!Array.isArray(requestParam)) {
        const timerId = setInterval(async () => {
          const response = await fetch(requestParam.url, requestParam.request.headers);
          let data = {};
          if (response && response.status < 400) {
            if (response.status !== 304) {
              data = await response.json();
            }
            clearInterval(timerId);
          } else {
            retryOptions.maxRetryAttempts--;
            if (retryOptions.maxRetryAttempts == 0) {
              clearInterval(timerId);
            }
            this.logger.error('startPolling', `Error in polling via retry`);
            throw new Error('Error while retrying.');
          }
          callback(data);
        }, retryOptions.retryInterval);
      } else {
        if (isPolling) {
          setInterval(async () => {
            await this.getData(requestParam, callback);
          }, DIRECTORY_POLLING_INTERVAL);
        } else await this.getData(requestParam, callback);
      }
    } else {
      this.logger.info('startPolling', `Normal startPolling triggered`);
      if (!Array.isArray(requestParam)) {
        try {
          const response = await fetch(requestParam.url, requestParam.request.headers);
          let data = {};
          if (response && response.status !== 304) data = await response.json();
          callback(data);
        } catch (error: any) {
          this.logger.error('startPolling', `Error in api call:-` + error.toString());
        }
      } else {
        if (isPolling) {
          setInterval(async () => {
            await this.getData(requestParam, callback);
          }, DIRECTORY_POLLING_INTERVAL);
        } else await this.getData(requestParam, callback);
      }
    }

    this.logger.info('startPolling', `startPolling executed on worker`);
  };

  /**
   * Perform api polling on web worker thread
   * @param request - api endpoint and request
   * @param callback - method to invoke on completion
   * @example
   * ```
   * getData(request, proxy(callback.bind(this)))
   * ```
   */
  public async getData(request: Array<HttpPollingRequestParam>, callback: (data: any) => void) {
    this.logger.info('getData', `getData triggered`);
    try {
      const reqArray = request;
      const res = await Promise.allSettled(reqArray.map((u) => fetch(u.url, u.request.headers)));
      const dataArray: PromiseSettledResult<any>[] = await Promise.allSettled(
        res.map((r: any) => r.status === 'fulfilled' && r.value.status === 200 && r.value.json())
      );
      dataArray.map(
        (d: any, idx: number) =>
          (request[idx].url = this.updateLastPollingTime(
            reqArray[idx].url,
            d?.value.lastPollTime ? d?.value.lastPollTime : d?.value.resultSet?.lastPollTime
          ))
      );
      const keyArray = reqArray.map((r) => r.key);
      const combinedResponse = new Map<string, any>();
      keyArray.forEach((key, idx) => {
        combinedResponse.set(key, dataArray[idx]);
      });
      callback(combinedResponse);
    } catch (error : any) {
      this.logger.error('getData', `Error in api call:-` + error.toString());
    }
    this.logger.info('getData', `getData executed on worker`);
  }

  /**
   * Used to update the last poll time in the url string
   * @param url- URL string whose param updatedSince needs to updated
   */
  private updateLastPollingTime(url: string, lastPollTime: string) {
    const newUrl = new URL(url);
    newUrl.searchParams.set('updatedSince', lastPollTime ? lastPollTime : new Date(0).toISOString());
    return newUrl.toString();
  }
}

expose(new UtilWorker());
